import React, { useEffect, useMemo, useState } from "react";
import { Circle, MapContainer, Marker, Popup, TileLayer, Tooltip, useMap, useMapEvents } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import Swal from "sweetalert2";
import "sweetalert2/dist/sweetalert2.min.css";
import { createLocation, listLocations, saveLocation } from "../services/locationService";
import "../styles/locationMaintenance.css";

function clamp(v, min, max) {
  return Math.max(min, Math.min(max, v));
}

const grayMarkerIcon = L.divIcon({
  className: "location-marker-gray",
  iconSize: [22, 22],
  iconAnchor: [11, 11],
});

function RecenterMap({ lat, lng, radius }) {
  const map = useMap();
  useEffect(() => {
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
    const r = Math.max(10, Number(radius || 100));
    const bounds = L.latLng(lat, lng).toBounds(r * 2);
    map.flyToBounds(bounds, {
      padding: [36, 36],
      maxZoom: 19,
      duration: 0.55,
    });
  }, [lat, lng, radius, map]);
  return null;
}

function MapClickUpdater({ onPick }) {
  useMapEvents({
    click: (ev) => {
      const lat = Number(ev?.latlng?.lat);
      const lng = Number(ev?.latlng?.lng);
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
      onPick?.(lat, lng);
    },
  });
  return null;
}

export default function LocationMaintenance() {
  const [rows, setRows] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState("");
  const [geoQuery, setGeoQuery] = useState("");
  const [geoLoading, setGeoLoading] = useState(false);
  const [geoResults, setGeoResults] = useState([]);
  const [form, setForm] = useState({
    id: "",
    name: "",
    lat: 0,
    lng: 0,
    radius: 100,
    active: true,
  });

  const loadRows = async () => {
    setLoading(true);
    try {
      const data = await listLocations();
      setRows(data);
      if (!selectedId && data.length) {
        setSelectedId(data[0].id);
      }
    } catch (e) {
      console.error("listLocations error:", e);
      setNotice("No se pudo cargar location.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRows();
  }, []);

  useEffect(() => {
    const selected = rows.find((x) => x.id === selectedId);
    if (!selected) return;
    setForm({
      id: selected.id,
      name: selected.name || selected.id,
      lat: selected.lat ?? 0,
      lng: selected.lng ?? 0,
      radius: selected.radius ?? 100,
      active: selected.active !== false,
    });
  }, [selectedId, rows]);

  const mapCenter = useMemo(() => {
    const lat = Number(form.lat);
    const lng = Number(form.lng);
    if (Number.isFinite(lat) && Number.isFinite(lng)) return [lat, lng];
    const first = rows[0];
    if (first && Number.isFinite(first.lat) && Number.isFinite(first.lng)) {
      return [first.lat, first.lng];
    }
    return [-13.522, -71.967];
  }, [form.lat, form.lng, rows]);

  const handleSave = async () => {
    if (saving) return;
    const id = String(form.id || "").trim();
    if (!id) {
      await Swal.fire({ icon: "warning", title: "Validacion", text: "El ID de local es obligatorio." });
      return;
    }
    if (!/^[a-zA-Z0-9_-]{2,40}$/.test(id)) {
      await Swal.fire({ icon: "warning", title: "Validacion", text: "ID invalido. Usa 2-40 caracteres: letras, numeros, _ o -." });
      return;
    }
    const lat = Number(form.lat);
    const lng = Number(form.lng);
    const radius = Number(form.radius);
    if (!Number.isFinite(lat) || lat < -90 || lat > 90) {
      await Swal.fire({ icon: "warning", title: "Validacion", text: "Latitud invalida (rango -90 a 90)." });
      return;
    }
    if (!Number.isFinite(lng) || lng < -180 || lng > 180) {
      await Swal.fire({ icon: "warning", title: "Validacion", text: "Longitud invalida (rango -180 a 180)." });
      return;
    }
    if (!Number.isFinite(radius) || radius < 10 || radius > 5000) {
      await Swal.fire({ icon: "warning", title: "Validacion", text: "Radio invalido (10 a 5000 metros)." });
      return;
    }
    setSaving(true);
    try {
      const existing = rows.find((x) => x.id === id);
      if (existing) {
        await saveLocation(id, form);
        await Swal.fire({ icon: "success", title: "Local actualizado" });
      } else {
        await createLocation(id, form);
        await Swal.fire({ icon: "success", title: "Local creado" });
      }
      await loadRows();
      setSelectedId(id);
    } catch (e) {
      console.error("save location error:", e);
      await Swal.fire({ icon: "error", title: "Error", text: e?.message || "No se pudo guardar el local." });
    } finally {
      setSaving(false);
    }
  };

  const handleUseMyLocation = () => {
    if (!navigator?.geolocation) {
      setNotice("Geolocalizacion no disponible en este navegador.");
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const lat = Number(pos?.coords?.latitude);
        const lng = Number(pos?.coords?.longitude);
        if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
          setNotice("No se pudo obtener coordenadas.");
          return;
        }
        setForm((f) => ({ ...f, lat: Number(lat.toFixed(6)), lng: Number(lng.toFixed(6)) }));
        setNotice("Ubicacion actual cargada.");
      },
      (err) => {
        setNotice(`No se pudo obtener ubicacion: ${err?.message || "error"}`);
      },
      { enableHighAccuracy: true, timeout: 8000 }
    );
  };

  const handleSearchAddress = async () => {
    const q = String(geoQuery || "").trim();
    if (!q) {
      setNotice("Ingresa una direccion o lugar para buscar.");
      return;
    }
    setGeoLoading(true);
    try {
      const cuscoCenter = { lat: -13.53195, lng: -71.96746 };
      // viewbox aproximado de Cusco para priorizar sin bloquear resultados fuera del área
      const cuscoViewbox = "-72.12,-13.42,-71.81,-13.62"; // lonW,latN,lonE,latS
      const url = `https://nominatim.openstreetmap.org/search?format=jsonv2&q=${encodeURIComponent(
        q
      )}&limit=12&addressdetails=0&countrycodes=pe&viewbox=${encodeURIComponent(cuscoViewbox)}&bounded=0`;
      const resp = await fetch(url, {
        headers: {
          Accept: "application/json",
        },
      });
      if (!resp.ok) {
        throw new Error(`Busqueda fallida (${resp.status})`);
      }
      const data = await resp.json();
      const listRaw = Array.isArray(data)
        ? data
            .map((x) => ({
              label: String(x?.display_name || "").trim(),
              lat: Number(x?.lat),
              lng: Number(x?.lon),
            }))
            .filter((x) => x.label && Number.isFinite(x.lat) && Number.isFinite(x.lng))
        : [];

      const norm = (s) =>
        String(s || "")
          .normalize("NFD")
          .replace(/[\u0300-\u036f]/g, "")
          .toLowerCase();

      const score = (item) => {
        const text = norm(item.label);
        let s = 0;
        if (text.includes("cusco")) s += 50;
        if (text.includes("cuzco")) s += 50;
        if (text.includes("peru")) s += 15;
        const dLat = item.lat - cuscoCenter.lat;
        const dLng = item.lng - cuscoCenter.lng;
        const dist2 = dLat * dLat + dLng * dLng;
        s += Math.max(0, 20 - dist2 * 8000);
        return s;
      };

      const list = listRaw
        .map((x) => ({ ...x, _score: score(x) }))
        .sort((a, b) => b._score - a._score)
        .slice(0, 5)
        .map(({ _score, ...rest }) => rest);

      setGeoResults(list);
      if (!list.length) {
        setNotice("No se encontraron resultados para la busqueda.");
      }
    } catch (e) {
      console.error("geocoding error:", e);
      setNotice(e?.message || "No se pudo buscar la direccion.");
    } finally {
      setGeoLoading(false);
    }
  };

  return (
    <div className="location-page">
      <div className="location-card">
        {notice ? <div style={{ marginBottom: 10, fontSize: 13, color: "#334155" }}>{notice}</div> : null}
        <div className="location-grid">
          <div className="location-form">
            <div className="location-form__section">
              <div className="location-form__section-title">Seleccion</div>
              <label>Local</label>
              <select
                value={selectedId}
                onChange={(e) => setSelectedId(e.target.value)}
                disabled={loading || saving || rows.length === 0}
              >
                {rows.length === 0 ? <option value="">(Sin locales)</option> : null}
                {rows.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.name} ({r.id})
                  </option>
                ))}
              </select>
            </div>

            <div className="location-form__section">
              <div className="location-form__section-title">Datos del local</div>
              <label>ID</label>
              <input
                value={form.id}
                onChange={(e) => setForm((f) => ({ ...f, id: e.target.value }))}
                placeholder="office3"
              />

              <label>Nombre</label>
              <input
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="Oficina 3"
              />
            </div>

            <div className="location-form__section">
              <div className="location-form__section-title">Coordenadas</div>
              <div className="location-coords-grid">
                <div>
                  <label>Latitud</label>
                  <input
                    type="number"
                    step="0.000001"
                    value={form.lat}
                    onChange={(e) => setForm((f) => ({ ...f, lat: Number(e.target.value) }))}
                  />
                </div>
                <div>
                  <label>Longitud</label>
                  <input
                    type="number"
                    step="0.000001"
                    value={form.lng}
                    onChange={(e) => setForm((f) => ({ ...f, lng: Number(e.target.value) }))}
                  />
                </div>
              </div>
            </div>

            <div className="location-form__section">
              <div className="location-form__section-title">Busqueda</div>
              <label>Buscar direccion</label>
              <div className="location-search-row">
                <input
                  value={geoQuery}
                  onChange={(e) => setGeoQuery(e.target.value)}
                  placeholder="Ej: Av. Tupac Amaru, Cusco"
                />
                <button
                  type="button"
                  className="btn ghost"
                  onClick={handleSearchAddress}
                  disabled={geoLoading}
                >
                  {geoLoading ? "Buscando..." : "Buscar"}
                </button>
              </div>
              {geoResults.length ? (
                <div style={{ border: "1px solid #e5e7eb", borderRadius: 8, maxHeight: 140, overflow: "auto" }}>
                  {geoResults.map((r, idx) => (
                    <button
                      key={`${r.lat}-${r.lng}-${idx}`}
                      type="button"
                      className="btn ghost"
                      style={{
                        width: "100%",
                        textAlign: "left",
                        border: "none",
                        borderBottom: "1px solid #eef2f7",
                        borderRadius: 0,
                        padding: "8px 10px",
                        fontWeight: 500,
                      }}
                      onClick={() => {
                        setForm((f) => ({
                          ...f,
                          lat: Number(r.lat.toFixed(6)),
                          lng: Number(r.lng.toFixed(6)),
                        }));
                        setGeoResults([]);
                        setNotice("Ubicacion cargada desde busqueda.");
                      }}
                    >
                      {r.label}
                    </button>
                  ))}
                </div>
              ) : null}
              <button type="button" className="btn ghost" onClick={handleUseMyLocation} disabled={saving}>
                Usar mi ubicacion
              </button>
            </div>

            <div className="location-form__section">
              <div className="location-form__section-title">Cobertura</div>
              <label>Radio (m)</label>
              <input
                type="number"
                min="1"
                value={form.radius}
                onChange={(e) => setForm((f) => ({ ...f, radius: Number(e.target.value) }))}
              />

              <label>Estado</label>
              <select
                value={form.active ? "true" : "false"}
                onChange={(e) => setForm((f) => ({ ...f, active: e.target.value === "true" }))}
              >
                <option value="true">Activo</option>
                <option value="false">Inactivo</option>
              </select>
            </div>

            <div className="location-form__section">
              <button type="button" className="btn primary" onClick={handleSave} disabled={saving}>
                {saving ? "Guardando..." : "Guardar local"}
              </button>
            </div>
          </div>

          <div className="location-preview">
            <MapContainer
              center={mapCenter}
              zoom={16}
              className="location-map"
              scrollWheelZoom
              zoomControl
            >
              <TileLayer
                attribution="&copy; OpenStreetMap contributors"
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                maxZoom={19}
              />
              <RecenterMap
                lat={Number(form.lat)}
                lng={Number(form.lng)}
                radius={Number(form.radius)}
              />
              <MapClickUpdater
                onPick={(lat, lng) =>
                  setForm((prev) => ({
                    ...prev,
                    lat: Number(lat.toFixed(6)),
                    lng: Number(lng.toFixed(6)),
                  }))
                }
              />
              <Circle
                center={[Number(form.lat) || mapCenter[0], Number(form.lng) || mapCenter[1]]}
                radius={Math.max(1, Number(form.radius || 100))}
                pathOptions={{
                  color: "#64748b",
                  fillColor: "#94a3b8",
                  fillOpacity: 0.18,
                  weight: 2,
                  className: "location-halo-leaflet",
                }}
              />
              <Marker
                position={[Number(form.lat) || mapCenter[0], Number(form.lng) || mapCenter[1]]}
                icon={grayMarkerIcon}
                draggable
                eventHandlers={{
                  dragend: (ev) => {
                    const ll = ev?.target?.getLatLng?.();
                    if (!ll) return;
                    setForm((prev) => ({
                      ...prev,
                      lat: Number(ll.lat.toFixed(6)),
                      lng: Number(ll.lng.toFixed(6)),
                    }));
                  },
                }}
              >
                <Popup>
                  {form.name || form.id || "Local"}<br />
                  Radio: {Math.max(1, Number(form.radius || 0))}m
                </Popup>
                <Tooltip permanent direction="top" offset={[0, -12]}>
                  {Math.max(1, Number(form.radius || 0))} m
                </Tooltip>
              </Marker>
            </MapContainer>
            <div className="location-preview__label">
              {form.name || form.id || "Local"} · radio {Math.max(1, Number(form.radius || 0))}m
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
